import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import Decimal from 'decimal.js';
import Numeric from '@/core/types/Numeric';

@Title_ru_RU_Class('Множитель')
class Multiplier extends Numeric {

  /**
   * SCALE = 4, а не 2, потому что множитель хранит ЕДИНИЦУ ПЛЮС отклонение,
   * и два знака съедали бы дробную часть процента: наценка 1.9% дала бы
   * 1.02 вместо 1.019, то есть 2%, а 0.5% превратились бы в 1%. Четыре знака
   * представляют процент с двумя знаками после запятой точно.
   * Соответствует toString, который печатает процент через toFixed(2).
   *
   * ВНИМАНИЕ, отличие от остальных Numeric: НАРУЖУ ТИП ХОДИТ ПРОЦЕНТОМ.
   * Множитель 1.019 — внутреннее представление, удобное для умножения; бек,
   * БД и пользователь оперируют процентом (1.9). Граница — пара fromServer
   * (процент → множитель) и toJSON (множитель → процент), обе переопределены
   * здесь. Зеркало на беке: Multiplier::fromData и jsonSerialize.
   * Внутренние пути (fromOperation, конструктор) принимают именно множитель.
   */
  static readonly SCALE = 4;

  /**
   * Гидрация с бека — ПРОЦЕНТ, а не множитель. Отличается от остальных
   * Numeric, где fromServer принимает внутреннее значение как есть:
   * там представление снаружи и внутри совпадает, здесь нет.
   */
  static fromServer(value: string): Multiplier {
    return Multiplier.fromPercent(value);
  }

  /** Результат вычисления — уже посчитанный Decimal. */
  static fromOperation(value: Decimal): Multiplier {
    return new Multiplier(value);
  }

  /**
   * Создание из процента наценки/скидки: 22 → 1.22, -10 → 0.90.
   * Обратная операция к asPercent.
   *
   * Принимает и Decimal, потому что asPercent возвращает именно его —
   * круговорот fromPercent(m.asPercent()) должен типизироваться.
   */
  static fromPercent(percent: Decimal.Value | null | undefined): Multiplier {
    return Multiplier.fromOperation(Multiplier.toDecimal(percent).div(100).plus(1));
  }

  /**
   * Сложение наценок: 20% + 5% = 25%, то есть 1.20 и 1.05 дают 1.25.
   *
   * ВНИМАНИЕ: в отличие от Sum.plus и Qty.plus складывает НЕ внутренние
   * значения, а отклонения от базы. Множитель — это единица плюс отклонение,
   * поэтому при сложении лишняя единица вычитается: 1.20 + 1.05 - 1 = 1.25.
   * Сложение «в лоб» дало бы 2.25.
   *
   * Последовательное применение наценок (1.20 × 1.05 = 1.26) — ДРУГАЯ
   * операция, и она даёт другой результат. Здесь её нет: не понадобилась.
   */
  plus(other: Multiplier | null | undefined): Multiplier {
    return Multiplier.fromOperation(this.value.plus(Multiplier.toDecimal(other)).minus(1));
  }

  /**
   * Множитель в виде процента отклонения от базы (100% = множитель 1).
   * 1.22 → 22, 0.90 → -10.
   */
  asPercent(): Decimal {
    return this.value.minus(1).times(100);
  }

  /**
   * Отображение — процент со знаком, а не сырой множитель.
   * Хранится 1.22, выводится "22%".
   */
  toString(): string {
    const percent = this.asPercent();
    return `${percent.toFixed(2)}%`;
  }

  /**
   * Сериализация — ПРОЦЕНТ, а не внутренний множитель. Обратная к fromServer:
   * что приняли, то и вернули.
   *
   * Строкой, как у всех Numeric: строка переносит значение и scale без потерь
   * на обоих концах (JS/PHP). Два знака — предел точности процента при
   * SCALE = 4. Пустое значение — null, как в Numeric.
   */
  toJSON(): string | null {
    if (this.isEmpty() === true) {
      return null;
    }
    else {
      return this.asPercent().toFixed(2);
    }
  }
}

export default Multiplier;